import posthog from 'posthog-js';
import { useSyncExternalStore } from 'react';

import { getSoundsEnabled } from '@/lib/sound';

/**
 * Global Pomodoro timer store. Module scope + `useSyncExternalStore` — the same
 * pattern as {@link ../chat/chat-launch-store} — so the timer keeps running
 * even while the panel is hidden (the interval lives here, not in a component).
 *
 * Timer correctness: while running we store an absolute {@link PomodoroState.endsAt}
 * timestamp and derive the remaining time from `Date.now()` on a 500ms interval —
 * we never decrement a per-tick counter, so the countdown survives background-tab
 * throttling. On pause we snapshot the remaining ms into {@link PomodoroState.remainingMs}.
 */

/**
 * PostHog tracking — a module store can't use `usePostHog`, so the initialized
 * singleton (providers/posthog runs `posthog.init` at module scope) is used
 * directly. Guarded on `__loaded` so pre-init calls and jsdom tests no-op.
 */
function track(event: string, props?: Record<string, unknown>) {
  try {
    if (posthog.__loaded) posthog.capture(event, props);
  } catch {
    // Tracking must never break the timer.
  }
}

/** The three classic pomodoro session kinds. */
export type PomodoroSessionType = 'focus' | 'shortBreak' | 'longBreak';

/** How the widget is presented on screen. `hidden` = fully closed (X). */
export type PomodoroView = 'hidden' | 'panel' | 'pill';

/** Default session durations, in minutes. Classic 25 / 5 / 15. */
export const DEFAULT_DURATIONS_MIN: Record<PomodoroSessionType, number> = {
  focus: 25,
  shortBreak: 5,
  longBreak: 15,
};

/** Allowed range for a user-configured duration, in minutes. */
export const MIN_DURATION_MIN = 1;
export const MAX_DURATION_MIN = 120;

const DURATIONS_STORAGE_KEY = 'flashka.pomodoro-durations';
const AUTOSTART_STORAGE_KEY = 'flashka.pomodoro-autostart';

/** Auto-start defaults ON: once you press Start, sessions keep chaining. */
export const DEFAULT_AUTO_START = true;

function readStoredAutoStart(): boolean {
  if (typeof window === 'undefined') return DEFAULT_AUTO_START;
  try {
    const raw = window.localStorage.getItem(AUTOSTART_STORAGE_KEY);
    if (raw === null) return DEFAULT_AUTO_START;
    return raw === 'true';
  } catch {
    // Storage unavailable (private mode) — fall back to the default.
    return DEFAULT_AUTO_START;
  }
}

function persistAutoStart(value: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTOSTART_STORAGE_KEY, String(value));
  } catch {
    // Persistence is best-effort; the in-memory value still applies.
  }
}

/** Clamp a minutes value to the allowed range, coercing NaN → the default. */
export function clampDurationMin(minutes: number, fallback: number): number {
  if (!Number.isFinite(minutes)) return fallback;
  return Math.min(MAX_DURATION_MIN, Math.max(MIN_DURATION_MIN, Math.round(minutes)));
}

function readStoredDurations(): Record<PomodoroSessionType, number> {
  if (typeof window === 'undefined') return { ...DEFAULT_DURATIONS_MIN };
  try {
    const raw = window.localStorage.getItem(DURATIONS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DURATIONS_MIN };
    const parsed = JSON.parse(raw) as Partial<Record<PomodoroSessionType, number>>;
    return {
      focus: clampDurationMin(parsed.focus ?? NaN, DEFAULT_DURATIONS_MIN.focus),
      shortBreak: clampDurationMin(
        parsed.shortBreak ?? NaN,
        DEFAULT_DURATIONS_MIN.shortBreak,
      ),
      longBreak: clampDurationMin(
        parsed.longBreak ?? NaN,
        DEFAULT_DURATIONS_MIN.longBreak,
      ),
    };
  } catch {
    // Malformed value or storage unavailable (private mode) — fall back to defaults.
  }
  return { ...DEFAULT_DURATIONS_MIN };
}

function persistDurations(durationsMin: Record<PomodoroSessionType, number>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DURATIONS_STORAGE_KEY, JSON.stringify(durationsMin));
  } catch {
    // Persistence is best-effort; the in-memory durations still apply.
  }
}

/** A long break replaces the short break after this many focus sessions. */
export const FOCUS_SESSIONS_PER_LONG_BREAK = 4;

/** localStorage key for the durable timer snapshot (persists across reloads/tabs). */
const TIMER_STORAGE_KEY = 'flashka.pomodoro-timer';

export type PomodoroState = {
  view: PomodoroView;
  /** The session the TIMER belongs to (running, or paused with progress). */
  sessionType: PomodoroSessionType;
  /**
   * The tab the user has selected. Purely a selection — moving between tabs
   * never touches the timer; Start (or Reset) is what commits `selectedType`
   * into `sessionType`. Widgets show a full-duration PREVIEW of the selected
   * type while it differs from the (non-running) active session — see
   * {@link displaySession}. Not persisted: a reload re-aligns it to
   * `sessionType`.
   */
  selectedType: PomodoroSessionType;
  running: boolean;
  /** Absolute end time (ms epoch) while running; null when paused/idle. */
  endsAt: number | null;
  /** Remaining time snapshot (ms) while paused; full duration when idle. */
  remainingMs: number;
  /** Completed focus sessions in the current cycle (0…FOCUS_SESSIONS_PER_LONG_BREAK). */
  completedFocus: number;
  /** User-configured session durations, in minutes. */
  durationsMin: Record<PomodoroSessionType, number>;
  /**
   * When true, a finished session immediately starts the next one (focus →
   * break → focus → …). When false, each session hands off PAUSED at full
   * duration, waiting for the user to press Start. Persisted separately from
   * the timer snapshot (its own localStorage key).
   */
  autoStart: boolean;
  /**
   * Monotonic counter bumped each time a segment's time runs out (in
   * {@link tick}). Not persisted — widgets watch it to fire a one-shot
   * attention shake when the count changes.
   */
  completions: number;
};

/** Duration of a session type in ms, from the current per-type minutes config. */
export function durationMs(
  durationsMin: Record<PomodoroSessionType, number>,
  type: PomodoroSessionType,
): number {
  return durationsMin[type] * 60 * 1000;
}

// ── Pure helpers (unit-tested) ──────────────────────────────────────────────

/**
 * Given the session that just finished and how many focus sessions were
 * completed before it, decide the next session type and the running
 * completed-focus count. A finished focus session increments the count and
 * routes to a long break every {@link FOCUS_SESSIONS_PER_LONG_BREAK}; a finished
 * break returns to focus (and resets the count to 0 after a long break).
 */
export function nextSession(
  finished: PomodoroSessionType,
  completedFocus: number,
): { sessionType: PomodoroSessionType; completedFocus: number } {
  if (finished === 'focus') {
    const nextCount = completedFocus + 1;
    const longBreakDue = nextCount % FOCUS_SESSIONS_PER_LONG_BREAK === 0;
    return {
      sessionType: longBreakDue ? 'longBreak' : 'shortBreak',
      completedFocus: nextCount,
    };
  }
  // A break finished — back to focus. Reset the cycle after a long break.
  return {
    sessionType: 'focus',
    completedFocus: finished === 'longBreak' ? 0 : completedFocus,
  };
}

/** Remaining ms derived from an absolute end time, clamped at 0. */
export function remainingFromEndsAt(endsAt: number, now: number): number {
  return Math.max(0, endsAt - now);
}

/** The fields produced by advancing past one or more elapsed sessions. */
export type AdvancedRun = Pick<
  PomodoroState,
  'sessionType' | 'completedFocus' | 'running' | 'endsAt' | 'remainingMs'
>;

/**
 * Advance a run whose {@link PomodoroState.endsAt} has already passed. Chains
 * through {@link nextSession} once per fully-elapsed session, extending the end
 * time by each new session's duration — anchored to the moment the previous
 * session ENDED (`endsAt`), never to `now`, so throttled/late ticks can't drift
 * the boundaries. Keeps running, landing INSIDE the first session whose window
 * still contains `now` (so a reload mid-cycle resumes the right session).
 *
 * Terminates because every session lasts ≥ {@link MIN_DURATION_MIN} minute, so
 * the running end time strictly increases and eventually passes `now`.
 */
export function advanceFromElapsed(
  sessionType: PomodoroSessionType,
  completedFocus: number,
  endsAt: number,
  now: number,
  durationsMin: Record<PomodoroSessionType, number>,
): AdvancedRun {
  let curType = sessionType;
  let curCompleted = completedFocus;
  let curEndsAt = endsAt;
  for (;;) {
    const next = nextSession(curType, curCompleted);
    curType = next.sessionType;
    curCompleted = next.completedFocus;
    curEndsAt += durationMs(durationsMin, curType);
    if (curEndsAt > now) {
      // Landed inside the first still-in-progress session — keep running.
      return {
        sessionType: curType,
        completedFocus: curCompleted,
        running: true,
        endsAt: curEndsAt,
        remainingMs: remainingFromEndsAt(curEndsAt, now),
      };
    }
    // This session also elapsed while away — keep chaining.
  }
}

/** The subset of {@link PomodoroState} we persist across reloads/tabs. */
export type PersistedTimer = Pick<
  PomodoroState,
  'view' | 'sessionType' | 'running' | 'endsAt' | 'remainingMs' | 'completedFocus'
>;

const SESSION_TYPES: PomodoroSessionType[] = ['focus', 'shortBreak', 'longBreak'];
const VIEWS: PomodoroView[] = ['hidden', 'panel', 'pill'];

/**
 * Parse a persisted timer snapshot, validating every field. Returns null for
 * anything malformed so the caller falls back to a fresh idle state. Kept pure
 * (no Date.now / localStorage) so it can be unit-tested directly.
 */
export function parsePersistedTimer(raw: string | null): PersistedTimer | null {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PersistedTimer>;
    if (
      !SESSION_TYPES.includes(p.sessionType as PomodoroSessionType) ||
      !VIEWS.includes(p.view as PomodoroView) ||
      typeof p.running !== 'boolean' ||
      !(
        p.endsAt === null ||
        (typeof p.endsAt === 'number' && Number.isFinite(p.endsAt))
      ) ||
      typeof p.remainingMs !== 'number' ||
      !Number.isFinite(p.remainingMs) ||
      typeof p.completedFocus !== 'number' ||
      !Number.isFinite(p.completedFocus)
    ) {
      return null;
    }
    return {
      view: p.view as PomodoroView,
      sessionType: p.sessionType as PomodoroSessionType,
      running: p.running,
      endsAt: p.endsAt ?? null,
      remainingMs: Math.max(0, p.remainingMs),
      completedFocus: Math.max(0, Math.round(p.completedFocus)),
    };
  } catch {
    return null;
  }
}

/**
 * Reconcile a persisted snapshot against the current time, producing the fields
 * the live store should start from. Pure — takes `now` and the durations config.
 *
 * - Not running → use the snapshot as-is (paused/idle survives verbatim).
 * - Running with time left → still running; re-derive `remainingMs` from `endsAt`.
 * - Running but `endsAt` already passed → mirror {@link tick}'s hand-off via
 *   {@link advanceFromElapsed}: resumes RUNNING inside the session whose window
 *   contains `now`.
 */
export function resumePersistedTimer(
  snapshot: PersistedTimer,
  now: number,
  durationsMin: Record<PomodoroSessionType, number>,
  autoStart: boolean = DEFAULT_AUTO_START,
): Pick<
  PomodoroState,
  'view' | 'sessionType' | 'running' | 'endsAt' | 'remainingMs' | 'completedFocus'
> {
  if (!snapshot.running || snapshot.endsAt === null) {
    return { ...snapshot, running: false, endsAt: null };
  }
  const remaining = remainingFromEndsAt(snapshot.endsAt, now);
  if (remaining > 0) {
    return { ...snapshot, remainingMs: remaining };
  }
  if (!autoStart) {
    // Tab was closed mid-run before tick() paused the hand-off — advance
    // exactly ONE session and land PAUSED at full duration (the classic
    // non-auto-start behavior). No chaining: a paused session never elapses.
    const next = nextSession(snapshot.sessionType, snapshot.completedFocus);
    return {
      view: snapshot.view,
      sessionType: next.sessionType,
      completedFocus: next.completedFocus,
      running: false,
      endsAt: null,
      remainingMs: durationMs(durationsMin, next.sessionType),
    };
  }
  // Auto-start: timer elapsed while away — advance like tick() does.
  const advanced = advanceFromElapsed(
    snapshot.sessionType,
    snapshot.completedFocus,
    snapshot.endsAt,
    now,
    durationsMin,
  );
  return { view: snapshot.view, ...advanced };
}

// ── Store ────────────────────────────────────────────────────────────────────

const initialDurations = readStoredDurations();
const initialAutoStart = readStoredAutoStart();

/** Fresh idle state at the given durations (nothing persisted / malformed). */
function idleState(
  durationsMin: Record<PomodoroSessionType, number>,
  autoStart: boolean,
): PomodoroState {
  return {
    view: 'hidden',
    sessionType: 'focus',
    selectedType: 'focus',
    running: false,
    endsAt: null,
    remainingMs: durationMs(durationsMin, 'focus'),
    completedFocus: 0,
    durationsMin,
    autoStart,
    completions: 0,
  };
}

function readInitialState(): PomodoroState {
  if (typeof window === 'undefined') return idleState(initialDurations, initialAutoStart);
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(TIMER_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode) — fall through to idle.
  }
  const snapshot = parsePersistedTimer(raw);
  if (!snapshot) return idleState(initialDurations, initialAutoStart);
  const resumed = resumePersistedTimer(
    snapshot,
    Date.now(),
    initialDurations,
    initialAutoStart,
  );
  // `selectedType` isn't persisted — a fresh load re-aligns it to the timer.
  return {
    ...resumed,
    selectedType: resumed.sessionType,
    durationsMin: initialDurations,
    autoStart: initialAutoStart,
    completions: 0,
  };
}

let state: PomodoroState = readInitialState();

function persistTimer(s: PomodoroState) {
  if (typeof window === 'undefined') return;
  try {
    const snapshot: PersistedTimer = {
      view: s.view,
      sessionType: s.sessionType,
      running: s.running,
      endsAt: s.endsAt,
      remainingMs: s.remainingMs,
      completedFocus: s.completedFocus,
    };
    window.localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Persistence is best-effort; the in-memory state still applies.
  }
}

const listeners = new Set<() => void>();
let ticker: ReturnType<typeof setInterval> | null = null;

function emit() {
  for (const listener of listeners) listener();
}

function setState(patch: Partial<PomodoroState>) {
  state = { ...state, ...patch };
  persistTimer(state);
  emit();
}

function ensureTicker() {
  if (ticker !== null) return;
  ticker = setInterval(tick, 500);
}

function stopTicker() {
  if (ticker === null) return;
  clearInterval(ticker);
  ticker = null;
}

/** localStorage key claiming a completion for tracking — every open tab runs
 * its own ticker off the same persisted run, so without this N tabs would
 * emit N Session-Completed events for one real completion. `endsAt` uniquely
 * identifies the run; read-check-write has no CAS but ticks are 500ms apart,
 * so a cross-tab race is vanishingly rare and costs one duplicate event. */
const COMPLETION_TRACK_KEY = 'flashka.pomodoro-tracked-completion';

function trackSessionCompleted() {
  try {
    const id = String(state.endsAt);
    if (window.localStorage.getItem(COMPLETION_TRACK_KEY) === id) return;
    window.localStorage.setItem(COMPLETION_TRACK_KEY, id);
  } catch {
    // Storage unavailable — still track (single-tab private mode).
  }
  track('Pomodoro-Session-Completed', {
    sessionType: state.sessionType,
    durationMin: state.durationsMin[state.sessionType],
    // Count INCLUDING the session that just completed (a finished focus
    // counts itself; nextSession increments only after this runs).
    completedFocus:
      state.sessionType === 'focus' ? state.completedFocus + 1 : state.completedFocus,
    autoStart: state.autoStart,
  });
}

// ponytail: fire-and-forget; play() rejection (autoplay-blocked) is harmless.
// play() returns a Promise in browsers but `undefined` under jsdom, so guard it.
function playCompleteSound() {
  // Respect the global "sound effects" preference — this chime keeps its
  // own Audio() mechanism but must go quiet with everything else.
  if (!getSoundsEnabled()) return;
  if (typeof Audio === 'undefined') return;
  void new Audio('/sounds/timer-complete.mp3').play()?.catch(() => {});
}

/** Drives the countdown and the automatic session hand-off at zero. */
function tick() {
  if (!state.running || state.endsAt === null) {
    stopTicker();
    return;
  }
  const now = Date.now();
  const remaining = remainingFromEndsAt(state.endsAt, now);
  if (remaining > 0) {
    // Re-derive; no per-tick decrement (survives tab throttling).
    setState({ remainingMs: remaining });
    return;
  }
  // Session finished — chime, then advance. With auto-start it keeps running
  // (endsAt anchored to the previous session's end, not `now`, so late/throttled
  // ticks don't drift); otherwise it hands off PAUSED at full duration and the
  // ticker stops. Either way the selection follows the new session.
  playCompleteSound();
  trackSessionCompleted();
  const completions = state.completions + 1;
  if (!state.autoStart) {
    const next = nextSession(state.sessionType, state.completedFocus);
    const fullMs = durationMs(state.durationsMin, next.sessionType);
    stopTicker();
    setState({
      sessionType: next.sessionType,
      selectedType: next.sessionType,
      completedFocus: next.completedFocus,
      running: false,
      endsAt: null,
      remainingMs: fullMs,
      completions,
    });
    return;
  }
  const advanced = advanceFromElapsed(
    state.sessionType,
    state.completedFocus,
    state.endsAt,
    now,
    state.durationsMin,
  );
  setState({
    sessionType: advanced.sessionType,
    selectedType: advanced.sessionType,
    completedFocus: advanced.completedFocus,
    running: advanced.running,
    endsAt: advanced.endsAt,
    remainingMs: advanced.remainingMs,
    completions,
  });
}

// A restored running timer (endsAt in the future) needs its interval running.
// Safe to call now: ensureTicker/tick are hoisted and `window` is guarded above.
if (typeof window !== 'undefined' && state.running && state.endsAt !== null) {
  ensureTicker();
}

export function startPomodoro() {
  if (state.running) return;
  // Resuming a paused run (vs starting fresh) — computed BEFORE the state
  // mutation. A committed tab-switch is always a fresh run.
  const resumed =
    state.selectedType === state.sessionType &&
    state.remainingMs < durationMs(state.durationsMin, state.sessionType);
  if (state.selectedType !== state.sessionType) {
    // Start COMMITS the selected tab as a fresh run (abandoning any paused
    // progress on the previous session).
    const remainingMs = durationMs(state.durationsMin, state.selectedType);
    setState({
      sessionType: state.selectedType,
      remainingMs,
      running: true,
      endsAt: Date.now() + remainingMs,
    });
  } else {
    setState({ running: true, endsAt: Date.now() + state.remainingMs });
  }
  ensureTicker();
  track('Pomodoro-Timer-Started', {
    sessionType: state.sessionType,
    durationMin: state.durationsMin[state.sessionType],
    autoStart: state.autoStart,
    // Distinguishes pause→resume from a fresh session start.
    resumed,
  });
}

export function pausePomodoro() {
  if (!state.running || state.endsAt === null) return;
  const remainingMs = remainingFromEndsAt(state.endsAt, Date.now());
  stopTicker();
  setState({ running: false, endsAt: null, remainingMs });
  track('Pomodoro-Timer-Paused', {
    sessionType: state.sessionType,
    remainingMin: Math.round(remainingMs / 60_000),
  });
}

export function togglePomodoro() {
  if (state.running) pausePomodoro();
  else startPomodoro();
}

/**
 * Skip the TIMER's current session as if it had finished, advancing to the next
 * one via {@link nextSession} (so a skipped focus counts toward the long-break
 * cycle, exactly like the auto-advance hand-off in {@link tick}). The next
 * session always starts running immediately at its full duration — skipping is
 * an explicit "move on", so it never waits for another Start press.
 */
export function skipToNextSession() {
  const next = nextSession(state.sessionType, state.completedFocus);
  track('Pomodoro-Session-Skipped', {
    from: state.sessionType,
    to: next.sessionType,
  });
  const fullMs = durationMs(state.durationsMin, next.sessionType);
  setState({
    sessionType: next.sessionType,
    selectedType: next.sessionType,
    completedFocus: next.completedFocus,
    running: true,
    endsAt: Date.now() + fullMs,
    remainingMs: fullMs,
  });
  ensureTicker();
}

/**
 * Completely restart the pomodoro cycle: stop the ticker and land idle at a
 * fresh, paused focus session with the completed-focus count zeroed.
 */
export function restartPomodoroCycle() {
  track('Pomodoro-Cycle-Restarted', {
    sessionType: state.sessionType,
    running: state.running,
  });
  stopTicker();
  setState({
    sessionType: 'focus',
    selectedType: 'focus',
    running: false,
    endsAt: null,
    remainingMs: durationMs(state.durationsMin, 'focus'),
    completedFocus: 0,
  });
}

/**
 * Switch the selected TAB. Pure selection — the timer (running or paused) is
 * never touched; Start/Reset are what commit the selection. Widgets preview
 * the selected type via {@link displaySession}.
 */
export function selectSession(sessionType: PomodoroSessionType) {
  if (sessionType === state.selectedType) return;
  setState({ selectedType: sessionType });
}

/**
 * What the widgets should DISPLAY: the running countdown, the paused/idle
 * run's remaining time, or — when the selected tab differs from the
 * (non-running) active session — a full-duration preview of the selected type.
 */
export function displaySession(s: PomodoroState): {
  sessionType: PomodoroSessionType;
  remainingMs: number;
  totalMs: number;
} {
  const showActive = s.running || s.selectedType === s.sessionType;
  const sessionType = showActive ? s.sessionType : s.selectedType;
  const totalMs = durationMs(s.durationsMin, sessionType);
  return { sessionType, remainingMs: showActive ? s.remainingMs : totalMs, totalMs };
}

/**
 * Update one session type's duration (in minutes, clamped to the allowed range)
 * and persist all durations. Lazy behavior: if the changed type is the *current*
 * session and it is not running, we snap `remainingMs` to the new full duration
 * so the visible clock reflects the change immediately. A running session, or a
 * different (inactive) session type, is left untouched — the new duration takes
 * effect the next time that session starts/resets.
 */
export function setSessionDuration(type: PomodoroSessionType, minutes: number) {
  const clamped = clampDurationMin(minutes, DEFAULT_DURATIONS_MIN[type]);
  const durationsMin = { ...state.durationsMin, [type]: clamped };
  persistDurations(durationsMin);
  const patch: Partial<PomodoroState> = { durationsMin };
  if (type === state.sessionType && !state.running) {
    patch.remainingMs = durationMs(durationsMin, type);
    patch.endsAt = null;
  }
  setState(patch);
}

/** Restore all durations AND auto-start to their classic defaults (persisted). */
export function resetDurations() {
  const durationsMin = { ...DEFAULT_DURATIONS_MIN };
  persistDurations(durationsMin);
  persistAutoStart(DEFAULT_AUTO_START);
  const patch: Partial<PomodoroState> = {
    durationsMin,
    autoStart: DEFAULT_AUTO_START,
  };
  if (!state.running) {
    patch.remainingMs = durationMs(durationsMin, state.sessionType);
    patch.endsAt = null;
  }
  setState(patch);
}

/** Toggle whether a finished session auto-starts the next one (persisted). */
export function setAutoStart(value: boolean) {
  if (value === state.autoStart) return;
  // No event here: this is also called programmatically (settings Cancel
  // revert, resetDurations). autoStart changes are captured by the tile's
  // change-diffed Pomodoro-Settings-Saved and ride on Started/Completed.
  persistAutoStart(value);
  setState({ autoStart: value });
}

/** `auto` marks programmatic transitions (e.g. the pill auto-surfacing on
 * navigation) so analytics can separate them from deliberate user clicks. */
export function setPomodoroView(view: PomodoroView, auto = false) {
  if (view === state.view) return;
  track('Pomodoro-View-Changed', { view, from: state.view, auto });
  setState({ view });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function getPomodoroState(): PomodoroState {
  return state;
}

export function usePomodoro(): PomodoroState {
  return useSyncExternalStore(subscribe, getPomodoroState, getPomodoroState);
}

/** Format ms → `mm:ss` (rounds up so the last second reads 00:01, not 00:00). */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
